import type { ActiveFilters, Dimension } from '@/types'
import type {
  FilterEntry,
  FilterQuery,
  FilterType,
  Query,
  Slice,
  TimeQuery,
  TimeWindow as ApiTimeWindow,
} from './apiTypes'
import { DEFAULT_SLICE } from './apiTypes'
import { toApiDimensionType } from './dimensionApiMap'

/** Map UI `ActiveFilters` keys to canonical API `FilterType` ids. */
const FILTER_KEY_TO_TYPE: Partial<Record<keyof ActiveFilters, FilterType>> = {
  continent:      'continent',
  country:        'country',
  region:         'region',
  city:           'city',
  studio:         'studio',
  game:           'game',
  brand:          'brand',
  streamKey:      'stream_id',
  sourceUrl:      'source_url',
  streamType:     'stream_type',
  browser:        'browser',
  os:             'os',
  deviceType:     'device_type',
  platform:       'platform',
  cdnEdge:        'cdn_edge',
}

/** Convert the UI's per-dimension `ActiveFilters` into the wire `FilterQuery`. */
export function toFilterQuery(
  filters: ActiveFilters,
  opts?: { streamId?: string | null },
): FilterQuery {
  const values: FilterEntry[] = []
  for (const key of Object.keys(FILTER_KEY_TO_TYPE) as Array<keyof ActiveFilters>) {
    const type = FILTER_KEY_TO_TYPE[key]
    if (!type) continue
    const arr = filters[key]
    if (!Array.isArray(arr)) continue
    for (const v of arr) {
      const s = String(v ?? '').trim()
      if (s) values.push({ type, value: s })
    }
  }
  return {
    values,
    severity: filters.severity ?? [0, 1, 2],
    streamId: opts?.streamId ?? null,
    userId:   filters.userId ?? null,
  }
}

/** Build a `TimeQuery` from the UI's time-mode + window state. */
export function toTimeQuery(opts: {
  timeMode: 'live' | 'historic'
  window: ApiTimeWindow | 'custom'
  historicTs: number | null | undefined
  historicFromTs?: number | null | undefined
}): TimeQuery {
  if (opts.timeMode === 'historic' && opts.window === 'custom') {
    const end = opts.historicTs
    const start = opts.historicFromTs
    if (typeof start === 'number' && typeof end === 'number') {
      return {
        mode: 'historic',
        window: 'custom',
        startTs: Math.min(start, end),
        endTs: Math.max(start, end),
      }
    }
  }
  const window: ApiTimeWindow | undefined =
    opts.window === 'custom' ? '1h' : opts.window
  if (opts.timeMode === 'historic' && opts.historicTs) {
    return { mode: 'historic', window, startTs: opts.historicTs, endTs: opts.historicTs }
  }
  return { mode: 'live', window }
}

/**
 * Build the unified `Query` envelope from the current UI state.
 *
 * Modes vs filters vs World:
 *  - World always queries `continent` geo aggregates (fixed map overview),
 *    scoped by sidebar + advanced filters — not by the Hex mode selector.
 *  - Hex mode `stream` (or a geo drill in stream mode) → `view = "stream"`.
 *  - Hex modes `studio` | `game` → `view = "dimension"` on that axis; with a
 *    geo drillNode the slice scopes it (server resolves geo×mode pair slices).
 *  - Deep dive → `view = "analysis"`.
 */
export function buildQuery(input: {
  view: 'world' | 'hex' | 'deep' | string
  dimension: Dimension
  drillNode: { dimension: 'continent' | 'country' | 'region'; value: string } | null
  drillStream: string | null
  filters: ActiveFilters
  timeMode: 'live' | 'historic'
  window: ApiTimeWindow | 'custom'
  historicTs: number | null | undefined
  historicFromTs?: number | null | undefined
  clientGrain?: 'continent' | 'country' | 'city'
  /** When true, use 6h offline grace; otherwise 5m (Active only). */
  includeInactive?: boolean
}): Query {
  const { view, dimension, drillNode, drillStream } = input
  const includeOffline = input.includeInactive ? '6h' : '5m'

  const isWorld = view === 'world'
  const isHexDrill = view === 'hex' && !!drillNode
  const isDeepDive = view === 'deep' || !!drillStream
  const isStreamDimension = dimension === 'stream' || dimension === 'user'

  const slice: Slice = drillNode
    ? { type: drillNode.dimension, value: drillNode.value }
    : { ...DEFAULT_SLICE }

  // Inject the drill bucket as a typed filter so the stream listing also
  // narrows by it (covers cases where parentSliceType isn't recorded yet).
  const filters = toFilterQuery(input.filters, { streamId: drillStream })
  // World / Hex filters never send userId (removed from UX); keep wire field null.
  filters.userId = null
  if (drillNode) {
    filters.values = [
      ...filters.values.filter((f) => !(f.type === drillNode.dimension && f.value === drillNode.value)),
      { type: drillNode.dimension as FilterType, value: drillNode.value },
    ]
  }

  const time = toTimeQuery({
    timeMode: input.timeMode,
    window: input.window,
    historicTs: input.historicTs,
    historicFromTs: input.historicFromTs,
  })

  // World: connected Studio → Edge → client clusters (precomputed grain).
  if (isWorld) {
    const grain =
      input.clientGrain === 'continent' || input.clientGrain === 'country' || input.clientGrain === 'city'
        ? input.clientGrain
        : 'city'
    return {
      view: 'world',
      dimension: 'continent',
      slice: { ...DEFAULT_SLICE },
      time,
      filters,
      limit: 2000,
      offset: 0,
      clientGrain: grain,
      includeOffline,
    }
  }

  const apiDimension = toApiDimensionType(isStreamDimension ? 'continent' : dimension)

  if (isDeepDive) {
    return {
      view: 'analysis',
      dimension: apiDimension,
      slice,
      time,
      filters,
      limit: 5000,
      offset: 0,
      includeOffline,
    }
  }

  // Geo-scoped studio/game: Hex keeps the active mode, scoped to the drilled
  // geo bucket (API resolves this via geo×mode pair slices, e.g. continent_studio).
  if (view === 'hex' && drillNode && (dimension === 'studio' || dimension === 'game')) {
    return {
      view: 'dimension',
      dimension: toApiDimensionType(dimension),
      slice,
      time,
      filters,
      limit: 2000,
      offset: 0,
      includeOffline,
    }
  }

  if (isHexDrill || isStreamDimension) {
    return {
      view: 'stream',
      dimension: apiDimension,
      slice,
      time,
      filters,
      limit: 5000,
      offset: 0,
      includeOffline,
    }
  }

  return {
    view: 'dimension',
    dimension: apiDimension,
    slice,
    time,
    filters,
    limit: 2000,
    offset: 0,
    includeOffline,
  }
}
